/**
 * The disclaimer of a room under the partner objection
 * (docs/partner-objection.md, REQ-06, REQ-12). The welcome sheet of the webapp
 * shows it, and the admin tab "Welcome & Disclaimer" previews and edits it.
 * Pure data, no deps: safe in a frontend bundle.
 */
import type { SupportedLanguage } from "./language.ts";

/**
 * The default text, for a room with no client disclaimer. Sasha and Agathe
 * wrote the English and French text on 2026-10-01, and Agathe revised it on
 * 2026-10-27 to name the Discovery and Resources sections. The other
 * languages read the English text until a validated translation exists. The
 * partner sentence applies only to a room with a partner.
 */
const DEFAULT_DISCLAIMER = {
  en: {
    base: "This space includes a Discovery section and a Resources section. The Discovery section offers non-promotional scientific monitoring reserved for healthcare professionals. Juisci selects the publications featured in it independently and under its sole responsibility.",
    partner:
      "[partner-name] provides institutional support for this section, without involvement in the selection or validation of its content, which does not constitute a recommendation for practice.",
  },
  fr: {
    base: "Cet espace comprend une section Discovery et une section Ressources. La section Discovery propose une veille scientifique non promotionnelle réservée aux professionnels de santé. Juisci y sélectionne les publications en toute indépendance et sous sa seule responsabilité.",
    partner:
      "[partner-name] apporte un soutien institutionnel à cette section, sans intervenir dans la sélection ni dans la validation des contenus, qui ne constituent pas une recommandation de pratique.",
  },
} as const;

/**
 * The marketing-authorisation sentence. The admin appends it to the
 * disclaimer only when the client asks for it (Figma O5).
 */
const MARKETING_AUTHORISATION_SENTENCE = {
  en: "The data presented may not have been validated by the competent health authorities in your country and may fall outside the marketing authorisation approved there.",
  fr: "Les données présentées peuvent ne pas avoir été validées par les autorités de santé compétentes de votre pays et sortir du cadre de l'autorisation de mise sur le marché qui y est accordée.",
} as const;

/**
 * The welcome sheet folds a longer disclaimer after its last whole sentence
 * within this length, behind "See more" (Figma O3, cells 07 and 08). The admin
 * tab warns past it. 480 is the length of the default text in French (462)
 * plus room for a partner name longer than its variable.
 */
export const DISCLAIMER_FOLD_LENGTH = 480;

type DisclaimerLanguage = keyof typeof DEFAULT_DISCLAIMER;

function disclaimerLanguage(language: SupportedLanguage): DisclaimerLanguage {
  return language === "fr" ? "fr" : "en";
}

/** The default text in `language`, with its `[partner-name]` variable. */
export function defaultRoomDisclaimer(
  language: SupportedLanguage,
  hasPartner: boolean,
): string {
  const text = DEFAULT_DISCLAIMER[disclaimerLanguage(language)];
  return hasPartner ? `${text.base} ${text.partner}` : text.base;
}

/** The marketing-authorisation sentence in `language`. */
export function marketingAuthorisationSentence(
  language: SupportedLanguage,
): string {
  return MARKETING_AUTHORISATION_SENTENCE[disclaimerLanguage(language)];
}

/**
 * Fills the variables of a disclaimer. `[organisation-name]` is the variable
 * of the old admin placeholder, and reads as the partner name.
 */
export function fillDisclaimerVariables(
  text: string,
  names: { roomName: string; partnerName: string },
): string {
  return text
    .replaceAll("[room-name]", names.roomName)
    .replaceAll("[partner-name]", names.partnerName)
    .replaceAll("[organisation-name]", names.partnerName);
}
